/**
 * Full Lead Flow Modal - Interactive Version
 * 
 * Displays all three flows (Call Center → Sales → Operations) in a large modal
 * with full drag & drop support and animated blue arrows.
 * 
 * Features:
 * - Entity type toggle (Leads/Projects)
 * - Drag & drop between stages within each flow
 * - Card click opens specific lead/project detail
 * - Animated flow arrows between lanes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Search, RefreshCw, ExternalLink, 
  Clock, MapPin, DollarSign, FileText, Briefcase, GripVertical
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { filterJobsByPermissions } from '../../utils/permissions';
import { 
  BOARD_FLOWS, 
  CALL_CENTER_STAGES, 
  SALES_STAGES, 
  OPERATIONS_STAGES,
  BOARD_STORAGE_KEYS,
  ENTITY_TYPES
} from '../../config/boardFlowsConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Entity options for dropdown
const ENTITY_OPTIONS = [
  { id: 'leads', name: 'Leads', icon: FileText },
  { id: 'projects', name: 'Projects', icon: Briefcase },
];

// ============================================
// ANIMATED S-CURVE FLOW ARROW SVG
// Creates an S-shaped path that flows like a roadmap:
// - Exits from bottom-right of previous board
// - Curves down and around (S-shape)
// - Points to top-left of next board
// ============================================
const FlowArrow = () => {
  return (
    <div className="relative h-16 w-full overflow-visible">
      <svg 
        className="w-full h-full" 
        viewBox="0 0 1000 64" 
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Gradient for the arrow path */}
          <linearGradient id="sArrowGradient" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.3" />
          </linearGradient>
          
          {/* Glow filter */}
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Arrowhead marker pointing down-left at the end */}
          <marker 
            id="sArrowheadDown" 
            markerWidth="14" 
            markerHeight="14" 
            refX="7" 
            refY="2" 
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <polygon 
              points="7 14, 0 0, 14 0" 
              fill="#3b82f6"
              filter="url(#glow)"
            />
          </marker>
        </defs>
        
        {/* S-curve roadmap style:
            Start at right side (end of prev board) → 
            Curve down on right → 
            Go left across → 
            Curve DOWN on left (pointing to next board) */}
        <path
          d="M 960 0 
             C 990 0, 1000 16, 1000 32 
             C 1000 48, 990 56, 960 56 
             L 40 56 
             C 10 56, 0 48, 0 64"
          fill="none"
          stroke="url(#sArrowGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          markerEnd="url(#sArrowheadDown)"
          className="animate-flow-pulse"
          filter="url(#glow)"
        />
        
        {/* Secondary decorative line */}
        <path
          d="M 960 0 
             C 990 0, 1000 16, 1000 32 
             C 1000 48, 990 56, 960 56 
             L 40 56 
             C 10 56, 0 48, 0 64"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="8,12"
          opacity="0.4"
        />
        
        {/* Animated dots traveling the S-curve path */}
        <circle r="6" fill="#60a5fa" filter="url(#glow)">
          <animateMotion
            dur="2.5s"
            repeatCount="indefinite"
            path="M 960 0 C 990 0, 1000 16, 1000 32 C 1000 48, 990 56, 960 56 L 40 56 C 10 56, 0 48, 0 64"
          />
        </circle>
        <circle r="5" fill="#93c5fd" filter="url(#glow)">
          <animateMotion
            dur="2.5s"
            repeatCount="indefinite"
            begin="0.8s"
            path="M 960 0 C 990 0, 1000 16, 1000 32 C 1000 48, 990 56, 960 56 L 40 56 C 10 56, 0 48, 0 64"
          />
        </circle>
        <circle r="4" fill="#bfdbfe" filter="url(#glow)">
          <animateMotion
            dur="2.5s"
            repeatCount="indefinite"
            begin="1.6s"
            path="M 960 0 C 990 0, 1000 16, 1000 32 C 1000 48, 990 56, 960 56 L 40 56 C 10 56, 0 48, 0 64"
          />
        </circle>
      </svg>
    </div>
  );
};

// ============================================
// DRAGGABLE MINI CARD
// ============================================
const MiniCard = ({ record, onClick, onDragStart, onDragEnd, isDragging, entityType }) => {
  // Leads have first_name/last_name, Jobs/Projects have customer_name
  const name = record.first_name 
    ? `${record.first_name} ${record.last_name || ''}`.trim()
    : (record.contact_name || record.customer_name || 'Unknown');
  const daysInStage = record.stage_changed_at 
    ? Math.floor((new Date() - new Date(record.stage_changed_at)) / (1000 * 60 * 60 * 24))
    : 0;
  
  const priorityColors = {
    urgent: 'border-l-4 border-l-red-500',
    high: 'border-l-4 border-l-orange-500',
    medium: 'border-l-4 border-l-yellow-500',
    low: 'border-l-4 border-l-green-500',
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, record)}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onClick(record);
      }}
      className={`bg-card rounded-lg p-2 mb-1.5 shadow-sm cursor-grab border transition-all hover:shadow-md hover:border-blue-400 ${
        priorityColors[record.priority] || ''
      } ${isDragging ? 'opacity-40 border-dashed border-blue-400 cursor-grabbing' : ''}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <span className="font-medium text-xs truncate">{name}</span>
        </div>
        <ExternalLink className="h-3 w-3 text-blue-500 flex-shrink-0" />
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
        {record.estimated_value && (
          <span className="font-semibold text-emerald-600">
            ${Number(record.estimated_value).toLocaleString()}
          </span>
        )}
        <div className="flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" />
          <span>{daysInStage}d</span>
        </div>
      </div>
      {record.address && (
        <div className="flex items-center gap-1 mt-1 text-[9px] text-muted-foreground">
          <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
          <span className="truncate">{record.address}</span>
        </div>
      )}
    </div>
  );
};

// ============================================
// SWIM LANE COLUMN WITH DRAG/DROP
// ============================================
const SwimLaneColumn = ({ 
  stage, 
  records, 
  flowType, 
  onCardClick, 
  entityType,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  draggedCard,
  dragOverStage
}) => {
  const stageRecords = records.filter(r => {
    const recordStage = r.stage || r.status || 'new';
    const mapping = BOARD_FLOWS[flowType]?.stageMapping || {};
    const normalizedStage = mapping[recordStage] || recordStage;
    return normalizedStage === stage.id;
  });
  
  const isDropTarget = dragOverStage === `${flowType}-${stage.id}`;
  
  return (
    <div 
      className={`flex-shrink-0 w-36 ${stage.isArchive ? 'opacity-50' : ''}`}
      onDragOver={onDragOver}
      onDragEnter={() => onDrop && onDrop(null, flowType, stage.id, true)}
      onDrop={(e) => onDrop && onDrop(e, flowType, stage.id, false)}
      data-testid={`flow-column-${flowType}-${stage.id}`}
    >
      <div className={`${stage.color} rounded-t-lg px-2 py-1.5 border-b transition-all ${
        isDropTarget ? 'ring-2 ring-blue-500 scale-[1.02]' : ''
      }`}>
        <div className="flex items-center justify-between">
          <span className="font-medium text-[10px] truncate">{stage.name}</span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">
            {stageRecords.length}
          </Badge>
        </div>
      </div>
      <div className={`bg-muted/30 rounded-b-lg p-1.5 min-h-[100px] max-h-[160px] overflow-y-auto transition-all ${
        isDropTarget ? 'bg-blue-50 dark:bg-blue-900/20' : ''
      }`}>
        {stageRecords.map((record) => (
          <MiniCard 
            key={record.id} 
            record={record} 
            onClick={onCardClick}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDragging={draggedCard?.id === record.id}
            entityType={entityType}
          />
        ))}
        {stageRecords.length === 0 && (
          <div className="text-center text-[10px] text-muted-foreground py-4">
            Empty
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// SWIM LANE ROW
// ============================================
const SwimLaneRow = ({ 
  flowType, 
  flowConfig, 
  records, 
  onCardClick, 
  entityType,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  draggedCard,
  dragOverStage
}) => {
  const stages = flowConfig.stages || [];
  
  return (
    <div className="mb-1">
      {/* Lane Header */}
      <div className={`${flowConfig.headerColor} text-white px-3 py-1.5 rounded-t-lg flex items-center justify-between`}>
        <div>
          <h3 className="font-semibold text-sm">{flowConfig.name}</h3>
          <p className="text-[10px] opacity-80">{flowConfig.description}</p>
        </div>
        <Badge variant="secondary" className="bg-white/20 text-white text-xs">
          {records.length} items
        </Badge>
      </div>
      
      {/* Lane Columns */}
      <div className="bg-muted/20 rounded-b-lg p-2 overflow-x-auto">
        <div className="flex gap-2">
          {stages.map((stage) => (
            <SwimLaneColumn
              key={stage.id}
              stage={stage}
              records={records}
              flowType={flowType}
              onCardClick={onCardClick}
              entityType={entityType}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDrop={onDrop}
              draggedCard={draggedCard}
              dragOverStage={dragOverStage}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export const FullLeadFlowModal = ({ open, onClose, entityType: initialEntityType = 'leads' }) => {
  const navigate = useNavigate();
  const { user, viewAsRole, viewAsUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  
  // Entity type selector with localStorage persistence
  const [entityType, setEntityType] = useState(() => {
    return localStorage.getItem(BOARD_STORAGE_KEYS.ENTITY_TYPE) || initialEntityType;
  });
  
  // Drag state
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [dragSourceFlow, setDragSourceFlow] = useState(null);

  // Handle entity type change
  const handleEntityTypeChange = (newEntity) => {
    setEntityType(newEntity);
    localStorage.setItem(BOARD_STORAGE_KEYS.ENTITY_TYPE, newEntity);
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!open) return;
    
    setLoading(true);
    try {
      const endpoint = entityType === 'projects' ? '/jobs' : '/leads';
      const [recordsRes, usersRes] = await Promise.all([
        axios.get(`${API}${endpoint}`),
        axios.get(`${API}/users`)
      ]);
      
      let fetchedRecords = recordsRes.data || [];
      const fetchedUsers = usersRes.data || [];
      setUsers(fetchedUsers);
      
      // Apply permission filtering
      let effectiveUser = user;
      if (viewAsUser && viewAsRole) {
        const testUser = fetchedUsers.find(u => u.id === viewAsUser);
        if (testUser) effectiveUser = testUser;
      }
      
      fetchedRecords = filterJobsByPermissions(fetchedRecords, effectiveUser, fetchedUsers);
      
      // For leads view, filter to only show leads that haven't converted to demo/sales
      if (entityType === 'leads') {
        fetchedRecords = fetchedRecords.filter(r => {
          const stage = r.stage || r.status || 'new';
          // Exclude leads that have been sold or converted to production
          const convertedStages = ['sold', 'demo_sold', 'signed', 'signed_agreement', 'contract_signed', 'production', 'to_production'];
          return !convertedStages.includes(stage);
        });
      }
      
      setRecords(fetchedRecords);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load flow data');
    } finally {
      setLoading(false);
    }
  }, [open, entityType, user, viewAsRole, viewAsUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter records
  const filteredRecords = records.filter(record => {
    // Leads have first_name/last_name, Jobs/Projects have customer_name
    const name = record.first_name 
      ? `${record.first_name} ${record.last_name || ''}`.trim()
      : (record.contact_name || record.customer_name || '');
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (record.address || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAssigned = filterAssignedTo === 'all' || record.assigned_to === filterAssignedTo;
    return matchesSearch && matchesAssigned;
  });

  // Categorize records by flow
  const getFlowRecords = (flowType) => {
    const stages = BOARD_FLOWS[flowType]?.stages || [];
    const stageIds = stages.map(s => s.id);
    const mapping = BOARD_FLOWS[flowType]?.stageMapping || {};
    
    return filteredRecords.filter(r => {
      const stage = r.stage || r.status || 'new';
      const normalizedStage = mapping[stage] || stage;
      return stageIds.includes(normalizedStage) || Object.keys(mapping).includes(stage);
    });
  };

  const callCenterRecords = getFlowRecords('call_center');
  const salesRecords = getFlowRecords('sales');
  const opsRecords = getFlowRecords('operations');

  // Handle card click - navigate to specific record detail
  const handleCardClick = (record) => {
    // Navigate to specific lead or project detail page based on entity type selection
    // Using the workspace records page with the specific record ID
    // Add view=detail to hide the list sidebar when coming from board
    const mode = entityType === 'projects' ? 'projects' : 'leads';
    const path = `/workspace/records?source=board&mode=${mode}&id=${record.id}&view=detail`;
    onClose();
    navigate(path);
  };

  // Drag handlers
  const handleDragStart = (e, record, flowType) => {
    setDraggedCard(record);
    setDragSourceFlow(flowType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverStage(null);
    setDragSourceFlow(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = async (e, targetFlow, targetStage, isEnter = false) => {
    if (isEnter) {
      setDragOverStage(`${targetFlow}-${targetStage}`);
      return;
    }
    
    e?.preventDefault();
    
    if (!draggedCard) {
      handleDragEnd();
      return;
    }

    const movedRecord = draggedCard;
    handleDragEnd();

    // Get backend stage mapping
    const getBackendStage = (flowType, stageId) => {
      if (flowType === 'call_center') {
        const mapping = {
          'new': 'new',
          'lead_received': 'lead_received',
          'lead_contacted': 'contacted',
          'callback_scheduled': 'callback',
          'appointment_set': 'appointment_set',
          'appointment_confirmed': 'appointment_confirmed',
          'transferred_to_sales': 'qualified',
          'no_contact': 'no_contact',
        };
        return mapping[stageId] || stageId;
      }
      
      if (flowType === 'sales') {
        const mapping = {
          'lead_assigned': 'lead',
          'demo_scheduled': 'appointment_set',
          'demo_confirmed': 'demo',
          'demo_completed': 'demo_completed',
          'proposal_sent': 'pending_signature',
          'demo_sold': 'sold',
          'to_production': 'production',
          'demo_no_sale': 'lost_dnq',
        };
        return mapping[stageId] || stageId;
      }
      
      if (flowType === 'operations') {
        const mapping = {
          'production': 'production',
          'scheduled': 'scheduled',
          'crew_assigned': 'crew_assigned',
          'in_progress': 'install',
          'install_complete': 'install_complete',
          'qc': 'qc',
          'invoiced': 'invoice',
          'pending_payment': 'pending_payment',
          'paid': 'paid',
        };
        return mapping[stageId] || stageId;
      }
      
      return stageId;
    };

    const newBackendStage = getBackendStage(targetFlow, targetStage);
    
    // Use entityType from component state to determine API endpoint
    // When viewing "leads", use /leads endpoint; when viewing "projects", use /jobs endpoint
    const endpoint = entityType === 'leads'
      ? `${API}/leads/${movedRecord.id}`
      : `${API}/jobs/${movedRecord.id}`;

    // Optimistic update
    setRecords(prev => prev.map(r => 
      r.id === movedRecord.id 
        ? { ...r, stage: newBackendStage, status: newBackendStage }
        : r
    ));

    try {
      // Leads use "status" field, projects use "stage" field
      const updateData = entityType === 'leads'
        ? { status: newBackendStage }
        : { stage: newBackendStage, status: newBackendStage };
      
      await axios.patch(endpoint, updateData);
      
      const stageName = BOARD_FLOWS[targetFlow]?.stages?.find(s => s.id === targetStage)?.name || targetStage;
      toast.success(`Moved to ${stageName}`);
    } catch (error) {
      // Revert on error
      setRecords(prev => prev.map(r => 
        r.id === movedRecord.id ? movedRecord : r
      ));
      console.error('Update stage error:', error);
      toast.error('Failed to update stage');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1600px] max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">Full Lead Flow</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Complete journey from lead intake to payment • Drag cards between stages
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Entity Type Selector */}
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium whitespace-nowrap">View:</Label>
                <Select value={entityType} onValueChange={handleEntityTypeChange}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="full-flow-entity-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_OPTIONS.map(entity => (
                      <SelectItem key={entity.id} value={entity.id}>
                        <div className="flex items-center gap-2">
                          <entity.icon className="h-4 w-4" />
                          <span>{entity.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-3 mt-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
            <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
              <SelectTrigger className="w-[180px] h-8">
                <SelectValue placeholder="Assigned To" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="text-xs text-muted-foreground ml-auto">
              {entityType === 'leads' && (
                <span className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                  Showing leads not yet converted to sales
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Flow Lanes */}
        <div className="flex-1 overflow-y-auto mt-3 space-y-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Call Center Lane */}
              <SwimLaneRow
                flowType="call_center"
                flowConfig={BOARD_FLOWS.call_center}
                records={callCenterRecords}
                onCardClick={handleCardClick}
                entityType={entityType}
                onDragStart={(e, record) => handleDragStart(e, record, 'call_center')}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                draggedCard={draggedCard}
                dragOverStage={dragOverStage}
              />
              
              {/* Arrow: Call Center → Sales */}
              <FlowArrow />
              
              {/* Sales Lane */}
              <SwimLaneRow
                flowType="sales"
                flowConfig={BOARD_FLOWS.sales}
                records={salesRecords}
                onCardClick={handleCardClick}
                entityType={entityType}
                onDragStart={(e, record) => handleDragStart(e, record, 'sales')}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                draggedCard={draggedCard}
                dragOverStage={dragOverStage}
              />
              
              {/* Arrow: Sales → Operations */}
              <FlowArrow />
              
              {/* Operations Lane */}
              <SwimLaneRow
                flowType="operations"
                flowConfig={BOARD_FLOWS.operations}
                records={opsRecords}
                onCardClick={handleCardClick}
                entityType={entityType}
                onDragStart={(e, record) => handleDragStart(e, record, 'operations')}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                draggedCard={draggedCard}
                dragOverStage={dragOverStage}
              />
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 pt-3 border-t mt-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Total: {filteredRecords.length} {entityType === 'leads' ? 'leads' : 'projects'} • 
              Call Center: {callCenterRecords.length} • 
              Sales: {salesRecords.length} • 
              Operations: {opsRecords.length}
            </span>
            <span className="text-blue-500">
              Drag cards to move between stages
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FullLeadFlowModal;
